import { DealDetails } from '@/classes/DealDetails'
import { Article } from '@/classes/Articles'
import { OutletItem } from '@/classes/OutletItem'
import settings from '@/settings'

type ContentItem = DealDetails | Article | OutletItem

interface ContentAdapter {
    getId: () => string | number | null
    getTitle: () => string
    getDescription: () => string
    getUrl: () => string
    getImageUrl: () => string
    getPrice: () => string
    getRetailPrice: () => string
    getDiscount: () => string
    hasPricing: () => boolean
    getFullSlug: () => string
}

function createAdapter(content: ContentItem | null): ContentAdapter | null {
    if (!content) return null

    if (content instanceof DealDetails || ('dealId' in content && typeof (content as any).dealId === 'number')) {
        const deal = content as DealDetails
        return {
            getId: () => deal.dealId || null,
            getTitle: () => deal.title || '',
            getDescription: () => deal.description || '',
            getUrl: () => `https://${settings.siteUrl}/Deal/${deal.dealId}`,
            getImageUrl: () => deal.imgUrl || '',
            getPrice: () => {
                if (deal.dealPrice !== undefined && deal.dealPrice >= 0) {
                    return `$${deal.dealPrice.toFixed(2)}`
                }
                return ''
            },
            getRetailPrice: () => {
                if (deal.retailPrice !== undefined && deal.retailPrice >= 0) {
                    return `$${deal.retailPrice.toFixed(2)}`
                }
                return ''
            },
            getDiscount: () => {
                if (deal.retailPrice && deal.dealPrice && deal.dealPrice >= 0 && deal.retailPrice >= 0) {
                    const discountPercent = Math.round((deal.retailPrice - deal.dealPrice) / deal.retailPrice * 100)
                    return `${discountPercent}%`
                }
                return ''
            },
            hasPricing: () => deal.dealPrice !== undefined && deal.dealPrice >= 0 && deal.dealPrice !== -1 && deal.dealPrice !== 0,
            getFullSlug: () => ''
        }
    }

    if (content instanceof Article || ('articleId' in content && typeof (content as any).articleId === 'number')) {
        const article = content as Article
        const articleUrl = article.isGiftList 
            ? `https://${settings.siteUrl}/gift-ideas/${article.articleId}/${article.permalink || article.prettyTitle || ''}`
            : `https://${settings.siteUrl}/list/${article.articleId}/${article.permalink || article.prettyTitle || ''}`
        return {
            getId: () => article.articleId || null,
            getTitle: () => article.title || '',
            getDescription: () => article.desc || '',
            getUrl: () => articleUrl,
            getImageUrl: () => article.imgUrl || '',
            getPrice: () => '', // Articles don't have prices
            getRetailPrice: () => '', // Articles don't have retail prices
            getDiscount: () => '', // Articles don't have discounts
            hasPricing: () => false,
            getFullSlug: () => ''
        }
    }

    if (content instanceof OutletItem || ('id' in content && typeof (content as any).id === 'string' && 'name' in content)) {
        const item = content as OutletItem
        // Use fullSlug if available (generated from ancestors), otherwise fall back to canonicalUrl or slug
        const outletUrl = item.fullSlug 
            ? `https://${settings.siteUrl}/outlet/${item.fullSlug}`
            : (item.canonicalUrl || (item.slug 
                ? `https://${settings.siteUrl}/outlet/${item.slug}`
                : `https://${settings.siteUrl}/outlet`))
        return {
            getId: () => item.id || null,
            getTitle: () => item.name || '',
            getDescription: () => item.description || item.subheading || '',
            getUrl: () => outletUrl,
            getImageUrl: () => item.imgSrc || item.bannerImgSrc || '',
            getPrice: () => '', // Outlet items don't have direct prices
            getRetailPrice: () => '', // Outlet items don't have retail prices
            getDiscount: () => '', // Outlet items don't have discounts
            hasPricing: () => false,
            getFullSlug: () => item.fullSlug || ''
        }
    }

    return null
}

export function replacePlaceholders(message: string, content: DealDetails | Article | OutletItem | null, tagline: string): string {
    const adapter = createAdapter(content)
    if (!adapter) {
        return message
    }

    let result = message

    // Replace {{dealId}} or {{articleId}} or {{id}}
    const id = adapter.getId()
    result = result.replace(/\{\{dealId\}\}/g, id ? id.toString() : '')
    result = result.replace(/\{\{articleId\}\}/g, id ? id.toString() : '')
    result = result.replace(/\{\{id\}\}/g, id ? id.toString() : '')

    // Replace {{title}}
    result = result.replace(/\{\{title\}\}/g, adapter.getTitle())

    // Replace {{description}}
    result = result.replace(/\{\{description\}\}/g, adapter.getDescription())

    // Replace {{price}} - formatted price (only for deals)
    result = result.replace(/\{\{price\}\}/g, adapter.getPrice())

    // Replace {{retailPrice}} - formatted retail price (only for deals)
    result = result.replace(/\{\{retailPrice\}\}/g, adapter.getRetailPrice())

    // Replace {{pricing}} - conditional pricing format (only for deals)
    if (!adapter.hasPricing()) {
        // Remove lines containing {{pricing}} while preserving line structure
        const lines = result.split(/\r?\n/)
        const filteredLines = lines.filter(line => !line.includes('{{pricing}}'))
        result = filteredLines.join('\n')
        // Clean up multiple consecutive newlines (more than 2) but preserve single newlines
        result = result.replace(/\n{3,}/g, '\n\n')
    } else {
        let pricing = ''
        const retailPrice = adapter.getRetailPrice()
        const dealPrice = adapter.getPrice()
        if (retailPrice && dealPrice) {
            // Has retail price: ~Was ${{retailPrice}}~ *NOW ${{price}}*
            pricing = `~Was ${retailPrice}~ *NOW ${dealPrice}*`
        } else if (dealPrice) {
            // No retail price: *ONLY ${{price}}*
            pricing = `*ONLY ${dealPrice}*`
        }
        result = result.replace(/\{\{pricing\}\}/g, pricing)
    }

    // Replace {{discount}} - discount percentage (only for deals)
    result = result.replace(/\{\{discount\}\}/g, adapter.getDiscount())

    // Replace {{url}} - content URL
    result = result.replace(/\{\{url\}\}/g, adapter.getUrl())

    // Replace {{tagline}}
    result = result.replace(/\{\{tagline\}\}/g, tagline)

    // Replace {{imageUrl}}
    result = result.replace(/\{\{imageUrl\}\}/g, adapter.getImageUrl())

    // Replace {{fullSlug}}
    result = result.replace(/\{\{fullSlug\}\}/g, adapter.getFullSlug())

    return result
}

